import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from 'react';
import { cn } from "utils/cn";
import { useAppSelector } from "../../store";
import { getClaroPending, getLoadingAIComments } from "../../store/tasksSlice";

interface TypingUser {
    user: string;
    timestamp: number;
}

const loadingAIPlaceholders = [
    "Your InnerCircle is thinking…",
    "Claro is rounding up assets…",
    "Looking at the screenshot…",
    "Refreshing the refresh…",
    "Inspecting code and design…",
    "Double-checking the obvious…",
    "Whispering to the pixels…",
    "Opening six tabs at once…",
    "Hmm.. Let's get on a call...",
    "Quietly judging spacing...",
    "Reviewing 2.5k-item checklist...",
    "Asking \"but what if...\"",
    "Exploring paths less clicked…",
    "Fixing what isn't broken…",
    "Losing an argument with a div…",
    "Waiting for caffeine…",
    "Accessing other tools…",
    "Dusting off old audits…",
    "Poking buttons to break them…",
    "Rethinking that one decision…",
    "Scanning every tag and title…",
    "On a silent group huddle…",
]

export function TypingIndicator({ taskId, className, indicatorClassName, onlyAI = false }: { taskId: string, className?: string, indicatorClassName?: string, onlyAI?: boolean }) {
    const loadingAiComments = useAppSelector(getLoadingAIComments);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(loadingAIPlaceholders[0]);
    const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
    const claroPendingMap = useAppSelector(getClaroPending);
    const claroPending = !!(taskId && claroPendingMap[taskId]);
    const hasAI = !!(taskId && (loadingAiComments[taskId] === 'running' || loadingAiComments[taskId] === 'pending') || claroPending);

    useEffect(() => {
        if (!taskId || !window.Echo) {
            setTypingUsers([]);
            return;
        }

        const channelName = `tasks.${taskId}`;
        const privateChannel = window.Echo.private(channelName);

        // Listen for comment typing whisper events
        privateChannel.listenForWhisper('comment-typing', (e: { user: string }) => {
            const timestamp = Date.now();
            setTypingUsers(prev => {
                // Remove existing entry for this user if any
                const filtered = prev.filter(u => u.user !== e.user);
                // Add new entry
                return [...filtered, { user: e.user, timestamp }];
            });
        });

        // Cleanup typing indicators older than 3 seconds
        const interval = setInterval(() => {
            const now = Date.now();
            setTypingUsers(prev =>
                prev.filter(u => now - u.timestamp < 3000)
            );
        }, 1000);

        return () => {
            clearInterval(interval);
            // Stop listening for whisper events when the component unmounts
            privateChannel.stopListeningForWhisper('comment-typing');
            window.Echo.leaveChannel(channelName);
        };
    }, [taskId]);

    useEffect(() => {
        if (hasAI) {
            const interval = setInterval(() => {
                setCurrentPlaceholder(loadingAIPlaceholders[Math.floor(Math.random() * loadingAIPlaceholders.length)]);
            }, 2000);

            return () => clearInterval(interval);
        }
    }, [hasAI]);

    const formatTypingMessage = () => {
        const users = typingUsers.map(u => u.user);
        const hasUsers = users.length > 0;

        if (claroPending) {
            return 'Claro is checking';
        }

        if (!hasUsers && !hasAI) return '';

        let message = '';

        if (onlyAI) {
            message = currentPlaceholder;
            return message;
        }

        // Format user typing message
        if (hasUsers || hasAI) {
            if (users.length === 1 && !hasAI) {
                message = `${users[0]} is typing`;
            } else if (users.length > 1 || users.length === 1 && hasAI) {
                message = `Multiple users are typing`;
            } else if (hasAI) {
                message = currentPlaceholder;
            }
        }

        return message;
    };

    const shouldShow = typingUsers.length > 0 || hasAI;

    return (
        <AnimatePresence>
            {shouldShow && (
                <motion.div
                    key="typing-indicator"
                    initial={{ opacity: 0, height: '0' }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: '0' }}
                    transition={{ duration: 0.2 }}
                    className={cn("text-sm text-gray-400 italic -mt-2 max-w-80 truncate flex gap-1.5", className)}
                >
                    <div className={cn('flex gap-0.5 justify-center items-center', indicatorClassName)}>
                        <div className={cn(
                            'h-1.5 w-1.5 rounded-[50%_15%_50%_50%] animate-typing-bounce [animation-delay:-0.3s]',
                            hasAI ? "bg-ata-orange/50" : "bg-gray-400")}></div>
                        <div className={cn(
                            'h-1.5 w-1.5 rounded-[50%_15%_50%_50%] animate-typing-bounce [animation-delay:-0.15s]',
                            hasAI ? "bg-ata-purple/50" : "bg-gray-400")}></div>
                        <div className={cn(
                            'h-1.5 w-1.5 rounded-[50%_15%_50%_50%] animate-typing-bounce',
                            hasAI ? "bg-ata-pink/50" : "bg-gray-400")}></div>
                    </div>
                    {formatTypingMessage()}
                </motion.div>
            )}
        </AnimatePresence>
    );
}